import React, { useState } from 'react';
import { ShieldCheckIcon, BriefcaseIcon, CheckCircleIcon, ChevronDownIcon, BookOpenIcon } from 'lucide-react';
import { SchoolListings } from '../components/SchoolListings';
import { useSchoolSearch } from '../hooks/useSchoolSearch';
import { useGeoLocation } from '../hooks/useGeoLocation';
import { useMetaTags } from '../hooks/useMetaTags';
import { programContent, getProgramContent } from '../data/programContent';
import { areaOfStudyData } from '../data/areaOfStudyData';
import { ContentSection } from '../components/ContentSection';
interface ProgramLandingPageProps {
  programId: number;
  degreeLevel: number;
  onProgramChange: (programId: number) => void;
}
export function ProgramLandingPage({
  programId,
  degreeLevel,
  onProgramChange
}: ProgramLandingPageProps) {
  const programData = getProgramContent(programId);
  const areaData = programData ? areaOfStudyData[programData.areaId] : areaOfStudyData[1];
  const {
    geo
  } = useGeoLocation();
  const {
    schools,
    loading,
    error
  } = useSchoolSearch({
    degreeInterest: degreeLevel,
    mProgram: programId,
    zipCode: geo.zipCode
  });
  // Use program-specific content if available, otherwise fall back to area content
  const headline = programData?.headline || areaData.headline;
  const description = programData?.description || areaData.description;
  useMetaTags({
    title: programData ? `${programData.name} Degree Programs | DegreeCompare` : `Online Degree Programs | DegreeCompare`,
    description: description,
    keywords: programData ? `online ${programData.name.toLowerCase()} degree, ${programData.name.toLowerCase()} programs, accredited ${programData.name.toLowerCase()} colleges` : 'online degree programs, accredited colleges',
    ogTitle: headline,
    ogDescription: description
  });
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().toLocaleString('default', {
    month: 'short'
  });
  return <div className="min-h-screen bg-gray-100" data-id="element-1114">
      {/* Hero Section */}
      <section className="w-full bg-[#1a2332] text-white py-8 md:py-12 px-4 relative overflow-hidden" data-id="element-1115">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#2a374a] to-transparent opacity-50 pointer-events-none" data-id="element-1116"></div>

        <div className="max-w-7xl mx-auto relative z-10" data-id="element-1117">
          <div className="max-w-3xl" data-id="element-1118">
            <div className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium text-gray-200 mb-3 border border-white/20" data-id="element-1119">
              <ShieldCheckIcon className="w-3.5 h-3.5" data-id="element-1120" />
              <span data-id="element-1121">
                Last Updated: {currentMonth} {currentYear}
              </span>
            </div>

            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold leading-tight mb-2" data-id="element-1122">
              {headline}
            </h1>

            <p className="text-sm md:text-base text-gray-300 max-w-xl" data-id="element-1123">
              {description}
            </p>
          </div>
        </div>
      </section>

      {/* School Listings */}
      <SchoolListings schools={schools} loading={loading} error={error} selectedAreaId={programData?.areaId || 1} selectedProgram={programId} onProgramChange={onProgramChange} data-id="element-1124" />

      {/* Program-Specific Content */}
      {programData && (programData.sections || programData.jobTitles || programData.faq) ? <section className="w-full bg-white border-t border-gray-200 py-16" data-id="element-1125">
          <div className="max-w-4xl mx-auto px-4" data-id="element-1126">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-1127">
              Is a Degree in {programData.name} Right for You?
            </h2>

            <div className="prose prose-lg max-w-none text-gray-700" data-id="element-1128">
              <p className="text-xl leading-relaxed mb-10 text-gray-600 text-center" data-id="element-1129">
                {programData.whyStudy}
              </p>

              {/* Career Outlook */}
              <div className="mb-12" data-id="element-1130">
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-100" data-id="element-1131">
                  <div className="flex items-center gap-3 mb-4" data-id="element-1132">
                    <div className="bg-blue-100 p-2 rounded-lg text-blue-600" data-id="element-1133">
                      <BriefcaseIcon className="w-6 h-6" data-id="element-1134" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 m-0" data-id="element-1135">
                      Career Outlook
                    </h3>
                  </div>
                  <p className="text-sm m-0" data-id="element-1136">{programData.careerOutlook}</p>
                </div>
              </div>

              {/* Job Titles */}
              {programData.jobTitles && programData.jobTitles.length > 0 && <div className="mb-12" data-id="element-1137">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1138">
                    Common Career Paths
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-1139">
                    {programData.jobTitles.map((title, idx) => <div key={idx} className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg border border-gray-100" data-id="element-1140">
                        <div className="w-2 h-2 rounded-full bg-[#3b82f6]" data-id="element-1141"></div>
                        <span className="font-medium text-gray-900" data-id="element-1142">
                          {title}
                        </span>
                      </div>)}
                  </div>
                </div>}

              {/* Program Sections */}
              {programData.sections && programData.sections.length > 0 && <div className="space-y-8 mb-12" data-id="element-1143">
                  {programData.sections.map((section, idx) => <div key={idx} data-id="element-1144">
                      <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1145">
                        {section.heading}
                      </h3>
                      <p className="text-base text-gray-700 leading-relaxed" data-id="element-1146">
                        {section.body}
                      </p>
                    </div>)}
                </div>}

              {/* What to Look For */}
              {programData.whatToLookFor && <div className="bg-gray-900 text-white rounded-2xl p-8 md:p-10 shadow-xl mb-12" data-id="element-1147">
                  <h3 className="text-2xl font-bold text-white mb-4 mt-0" data-id="element-1148">
                    What to Look For in a Program
                  </h3>
                  <div className="flex items-start gap-4" data-id="element-1149">
                    <CheckCircleIcon className="w-8 h-8 text-[#22c55e] flex-shrink-0 mt-1" data-id="element-1150" />
                    <p className="text-gray-300 text-lg leading-relaxed m-0" data-id="element-1151">
                      {programData.whatToLookFor}
                    </p>
                  </div>
                </div>}

              {/* FAQ Section */}
              {programData.faq && programData.faq.length > 0 && <div className="bg-white rounded-xl border border-gray-200 p-6 md:p-8" data-id="element-1152">
                  <div className="flex items-center gap-2 mb-5" data-id="element-1153">
                    <BookOpenIcon className="w-5 h-5 text-[#e8594f]" data-id="element-1154" />
                    <h3 className="text-lg font-bold text-gray-900" data-id="element-1155">
                      Frequently Asked Questions
                    </h3>
                  </div>

                  <div className="space-y-1" data-id="element-1156">
                    {programData.faq.map((item, idx) => <FaqItem key={idx} question={item.question} answer={item.answer} defaultOpen={idx === 0} data-id="element-1157" />)}
                  </div>
                </div>}
            </div>
          </div>
        </section> : <ContentSection areaData={areaData} data-id="element-1158" />}
    </div>;
}
function FaqItem({
  question,
  answer,
  defaultOpen
}: {
  question: string;
  answer: string;
  defaultOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen || false);
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-1159">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between py-3 text-left hover:text-[#3b82f6] transition-colors" data-id="element-1160">
        <span className="text-sm font-semibold text-gray-900 pr-4" data-id="element-1161">
          {question}
        </span>
        <ChevronDownIcon className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-1162" />
      </button>
      <div className={`overflow-hidden transition-all duration-200 ${isOpen ? 'max-h-40 opacity-100 pb-3' : 'max-h-0 opacity-0'}`} data-id="element-1163">
        <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1164">{answer}</p>
      </div>
    </div>;
}